import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import api from "@/lib/api";
import ScriptCard from "@/components/ScriptCard";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = [
  { key: "all", label: "All" },
  { key: "instagram-reel", label: "Instagram Reel" },
  { key: "youtube-ad", label: "YouTube Ad" },
  { key: "ugc", label: "UGC" },
  { key: "tiktok", label: "TikTok" },
  { key: "viral-hook", label: "Viral Hook" },
  { key: "brand-story", label: "Brand Story" },
];

export default function Marketplace() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sort, setSort] = useState("popular");
  const [scripts, setScripts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchScripts = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/scripts", { params: { q, category, sort } });
      setScripts(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchScripts(); /* eslint-disable-next-line */ }, [category, sort]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 min-h-screen">
      <div className="cw-label">Marketplace</div>
      <h1 className="font-display text-5xl mt-3">Every script we ship, tested on real ad spend.</h1>

      <div className="mt-10 grid lg:grid-cols-[1fr_auto] gap-4 items-center">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input
            data-testid="marketplace-search"
            placeholder="Search scripts, industries, hooks…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && fetchScripts()}
            className="pl-11 h-12 bg-black/40 border-white/10 text-white placeholder:text-white/30"
          />
        </div>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="marketplace-sort" className="w-full lg:w-48 h-12 bg-black/40 border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0F0F0F] border-white/10 text-white">
            <SelectItem value="popular">Popular</SelectItem>
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="price_asc">Price: Low → High</SelectItem>
            <SelectItem value="price_desc">Price: High → Low</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            data-testid={`filter-${c.key}`}
            onClick={() => setCategory(c.key)}
            className={`text-sm px-4 py-2 rounded-full border transition ${
              category === c.key
                ? "bg-[#FF6B00] border-[#FF6B00] text-white"
                : "border-white/10 text-white/70 hover:border-[#FF6B00]/50 hover:text-white"
            }`}
          >{c.label}</button>
        ))}
      </div>

      <div className="mt-10">
        {loading ? (
          <div className="text-white/50 py-20 text-center">Loading scripts…</div>
        ) : scripts.length === 0 ? (
          <div className="text-white/50 py-20 text-center">No scripts found.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {scripts.map((s, i) => <ScriptCard key={s.script_id} script={s} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
